import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import {
  User,
  Crown,
  Mail,
  Calendar,
  CreditCard,
  LogOut,
  CheckCircle,
  Package,
  Settings,
  XCircle,
  RefreshCw,
  Loader2,
  Lock,
} from "lucide-react";
import { format } from "date-fns";

const AccountPage = ({ openAuthModal }) => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, isMember, token, refreshUser } = useAuth();
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showCardForm, setShowCardForm] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [cardData, setCardData] = useState({
    card_name: "",
    card_number: "",
    card_expiry_month: "",
    card_expiry_year: "",
    card_cvn: "",
  });

  useEffect(() => {
    const fetchMembership = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API}/membership/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setMembership(response.data);
      } catch (error) {
        console.error("Failed to fetch membership:", error);
      }
    };
    
    const fetchPaymentConfig = async () => {
      try {
        const res = await axios.get(`${API}/payment/status`);
        setPaymentConfig(res.data);
      } catch (error) {
        console.error("Failed to fetch payment config:", error);
      }
    };

    fetchMembership();
    fetchPaymentConfig();
  }, [token]);

  const handleCancel = async () => {
    if (!confirm("Cancel auto-renewal? Your membership will remain active until the end of the current period.")) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/membership/cancel`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Auto-renewal cancelled. Membership active until expiry.");
      setMembership((m) => ({ ...m, subscription_cancelled: true, auto_renew: false }));
      if (refreshUser) await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/membership/reactivate`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Auto-renewal re-activated!");
      setMembership((m) => ({ ...m, subscription_cancelled: false, auto_renew: true }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reactivate.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpgrade = async () => {
    // Validate card fields
    if (!cardData.card_name || !cardData.card_number || !cardData.card_expiry_month || !cardData.card_expiry_year || !cardData.card_cvn) {
      toast.error("Please fill in all card details");
      return;
    }
    setActionLoading(true);
    try {
      // Step 1: Get eWay access code from backend
      const response = await axios.post(`${API}/membership/subscribe`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { form_action_url, access_code } = response.data;
      if (!form_action_url || !access_code) {
        toast.error("Failed to initialize payment. Please try again.");
        setActionLoading(false);
        return;
      }

      // Step 2: Submit card details directly to eWay via hidden form
      const form = document.createElement("form");
      form.method = "POST";
      form.action = form_action_url;
      form.style.display = "none";

      const fields = {
        EWAY_ACCESSCODE: access_code,
        EWAY_PAYMENTTYPE: "Credit Card",
        EWAY_CARDNAME: cardData.card_name,
        EWAY_CARDNUMBER: cardData.card_number.replace(/\s/g, ""),
        EWAY_CARDEXPIRYMONTH: cardData.card_expiry_month.padStart(2, "0"),
        EWAY_CARDEXPIRYYEAR: cardData.card_expiry_year,
        EWAY_CARDCVN: cardData.card_cvn,
      };

      Object.entries(fields).forEach(([name, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = value;
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
      // Browser navigates to eWay, then eWay redirects to /membership/success
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start membership upgrade.");
      setActionLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  if (!isAuthenticated) {
    return (
      <div className="container-main py-16 text-center" data-testid="account-login-prompt">
        <User className="w-20 h-20 text-slate-300 mx-auto mb-6" />
        <h1 className="text-2xl font-bold text-slate-900 font-['Outfit'] mb-2">Sign In to Your Account</h1>
        <p className="text-slate-600 mb-8">Access your orders, membership, and account settings</p>
        <div className="flex gap-4 justify-center">
          <Button variant="outline" onClick={() => openAuthModal("login")}>Sign In</Button>
          <Button className="btn-accent" onClick={() => openAuthModal("register")}>Create Account</Button>
        </div>
      </div>
    );
  }

  const isActive = membership?.is_member;
  const isCancelled = membership?.subscription_cancelled;
  const daysRemaining = membership?.days_remaining || 0;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="account-page">
      <div className="container-main py-8">
        <h1 className="text-2xl font-bold text-slate-900 font-['Outfit'] mb-6">My Account</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {/* User Info */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-slate-900 rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">{user?.full_name?.charAt(0).toUpperCase()}</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-semibold text-slate-900">{user?.full_name}</h2>
                    {isActive ? (
                      <span className="member-badge flex items-center gap-1"><Crown className="w-3 h-3" /> Member</span>
                    ) : (
                      <span className="guest-badge">Guest</span>
                    )}
                  </div>
                  <p className="text-slate-500 flex items-center gap-2 mt-1"><Mail className="w-4 h-4" /> {user?.email}</p>
                  <p className="text-sm text-slate-400 mt-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4" /> Member since {format(new Date(user?.created_at || new Date()), "MMMM yyyy")}
                  </p>
                </div>
              </div>
            </div>

            {/* Membership Management */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className={`p-6 text-white ${isActive ? "bg-gradient-to-r from-sky-500 to-blue-600" : "bg-gradient-to-r from-slate-700 to-slate-800"}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Crown className="w-5 h-5" />
                      {isActive ? "BundleCart Membership" : "Become a Member"}
                    </h3>
                    {isActive && membership?.subscription_expiry && (
                      <p className="text-sky-100 text-sm mt-1">
                        {isCancelled ? "Expires" : "Renews"}: {format(new Date(membership.subscription_expiry), "PPP")}
                        {isCancelled && <span className="ml-2 text-yellow-200 text-xs font-medium">(Cancelled)</span>}
                      </p>
                    )}
                    {!isActive && <p className="text-slate-300 text-sm mt-1">Just $5/month</p>}
                  </div>
                  <div className="text-right">
                    <span className="text-3xl font-bold">$5</span>
                    <span className="opacity-75">/month</span>
                  </div>
                </div>
              </div>

              <div className="p-6">
                {isActive && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-slate-700">Subscription Status</span>
                      <span className={`text-sm font-medium px-2 py-0.5 rounded-full ${isCancelled ? "bg-yellow-100 text-yellow-700" : "bg-emerald-100 text-emerald-700"}`}>
                        {isCancelled ? "Cancelling" : "Active"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-slate-700">Auto-Renewal</span>
                      <span className={`text-sm font-medium ${isCancelled ? "text-red-600" : "text-emerald-600"}`}>
                        {isCancelled ? "Off" : "On"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700">Days Remaining</span>
                      <span className="text-sm font-semibold text-slate-900">{daysRemaining} days</span>
                    </div>
                  </div>
                )}

                <h4 className="font-semibold text-slate-900 mb-4">Member Benefits</h4>
                <ul className="space-y-3 mb-6">
                  {["4% convenience fee (vs 7% for guests)", "$15 fee cap on all orders", "Priority email support", "Exclusive member-only deals (coming soon)"].map((benefit, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className={`w-5 h-5 mt-0.5 ${isActive ? "text-green-500" : "text-slate-300"}`} />
                      <span className={isActive ? "text-slate-700" : "text-slate-500"}>{benefit}</span>
                    </li>
                  ))}
                </ul>

                <Separator className="my-4" />

                {isActive ? (
                  <div className="space-y-3">
                    {isCancelled ? (
                      <Button className="w-full btn-primary" onClick={handleReactivate} disabled={actionLoading} data-testid="reactivate-btn">
                        {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                        Re-enable Auto-Renewal
                      </Button>
                    ) : (
                      <Button variant="outline" className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200" onClick={handleCancel} disabled={actionLoading} data-testid="cancel-membership-btn">
                        {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                        Cancel Auto-Renewal
                      </Button>
                    )}
                    <p className="text-xs text-slate-500 text-center">
                      {isCancelled
                        ? "Re-enabling will automatically renew your membership at the end of the current period."
                        : "Cancellation takes effect at the end of your current billing period."}
                    </p>
                  </div>
                ) : (
                  <Button className="w-full btn-accent" onClick={() => setShowCardForm(true)} disabled={actionLoading} data-testid="upgrade-membership-btn">
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Crown className="w-4 h-4 mr-2" />}
                    Upgrade to Member - $5/month
                  </Button>
                )}
              </div>
            </div>

            {/* Quick Links */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Quick Links</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button variant="outline" className="justify-start" onClick={() => navigate("/orders")} data-testid="view-orders-btn">
                  <Package className="w-4 h-4 mr-2" /> View Orders
                </Button>
                <Button variant="outline" className="justify-start" onClick={() => navigate("/products")}>
                  <Settings className="w-4 h-4 mr-2" /> Browse Products
                </Button>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Fee Comparison</h3>
              <div className="space-y-4">
                <div className={`p-4 rounded-lg ${isActive ? "bg-sky-50 border-2 border-sky-200" : "bg-slate-50"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-slate-900 flex items-center gap-2">
                      <Crown className={`w-4 h-4 ${isActive ? "text-sky-600" : "text-slate-400"}`} /> Member
                    </span>
                    {isActive && <span className="text-xs text-sky-600 font-medium">YOUR PLAN</span>}
                  </div>
                  <p className="text-sm text-slate-600">4% convenience fee</p>
                  <p className="text-sm text-slate-600">$15 cap</p>
                  <p className="text-sm text-slate-600">$10 flat delivery</p>
                </div>
                <div className={`p-4 rounded-lg ${!isActive ? "bg-slate-100 border-2 border-slate-300" : "bg-slate-50"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-slate-900">Guest</span>
                    {!isActive && <span className="text-xs text-slate-500 font-medium">YOUR PLAN</span>}
                  </div>
                  <p className="text-sm text-slate-600">7% convenience fee</p>
                  <p className="text-sm text-slate-600">$15 cap</p>
                  <p className="text-sm text-slate-600">$10 flat delivery</p>
                </div>
              </div>
              {!isActive && (
                <div className="mt-4 p-3 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-800"><strong>Save 5%</strong> on every order by becoming a member!</p>
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6">
              <Button variant="outline" className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" /> Sign Out
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Card Payment Modal */}
      {showCardForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="card-form-modal">
          <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-r from-sky-500 to-blue-600 p-5 text-white">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Crown className="w-5 h-5" /> Upgrade to Member
              </h2>
              <p className="text-sky-100 text-sm mt-1">$5.00 AUD/month - Save 3% on every order</p>
            </div>
            <div className="p-5 space-y-4">
              {paymentConfig?.eway_sandbox && paymentConfig?.test_card && (
                <div className="text-xs bg-yellow-50 border border-yellow-200 rounded p-2" data-testid="test-card-info">
                  <strong>Test Card:</strong> {paymentConfig.test_card.number} | Exp: {paymentConfig.test_card.expiry} | CVN: {paymentConfig.test_card.cvn}
                </div>
              )}
              <div>
                <Label className="text-xs text-slate-600">Name on Card <span className="text-red-500">*</span></Label>
                <Input
                  placeholder="John Smith"
                  value={cardData.card_name}
                  onChange={(e) => setCardData({...cardData, card_name: e.target.value})}
                  className="mt-1"
                  data-testid="membership-card-name"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600">Card Number <span className="text-red-500">*</span></Label>
                <Input
                  placeholder="4444 3333 2222 1111"
                  value={cardData.card_number}
                  onChange={(e) => setCardData({...cardData, card_number: e.target.value})}
                  className="mt-1"
                  data-testid="membership-card-number"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-slate-600">Month <span className="text-red-500">*</span></Label>
                  <Input
                    placeholder="12"
                    maxLength={2}
                    value={cardData.card_expiry_month}
                    onChange={(e) => setCardData({...cardData, card_expiry_month: e.target.value})}
                    className="mt-1"
                    data-testid="membership-card-month"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Year <span className="text-red-500">*</span></Label>
                  <Input
                    placeholder="28"
                    maxLength={2}
                    value={cardData.card_expiry_year}
                    onChange={(e) => setCardData({...cardData, card_expiry_year: e.target.value})}
                    className="mt-1"
                    data-testid="membership-card-year"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">CVN <span className="text-red-500">*</span></Label>
                  <Input
                    placeholder="123"
                    maxLength={4}
                    type="password"
                    value={cardData.card_cvn}
                    onChange={(e) => setCardData({...cardData, card_cvn: e.target.value})}
                    className="mt-1"
                    data-testid="membership-card-cvn"
                  />
                </div>
              </div>
              <p className="text-[10px] text-slate-400 flex items-center gap-1">
                <Lock className="w-3 h-3" /> Card details are sent directly to the Payment Gateway - never stored on our servers
              </p>
              <div className="flex gap-2 pt-2">
                <Button className="flex-1 btn-accent" onClick={handleUpgrade} disabled={actionLoading} data-testid="confirm-upgrade-btn">
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
                  Pay $5.00
                </Button>
                <Button variant="outline" onClick={() => setShowCardForm(false)} disabled={actionLoading}>Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountPage;
